import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality, Blob, Type } from '@google/genai';
import { ConnectionStatus, Message, LegalSummary } from './types';
import './LawyerPageVani.scss';

// --- Helper Functions for Audio Processing ---

function decode(base64: string): Uint8Array {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

function encode(bytes: Uint8Array): string {
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

async function decodeAudioData(
    data: Uint8Array,
    ctx: AudioContext,
    sampleRate: number,
    numChannels: number,
): Promise<AudioBuffer> {
    const dataInt16 = new Int16Array(data.buffer);
    const frameCount = dataInt16.length / numChannels;
    const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

    for (let channel = 0; channel < numChannels; channel++) {
        const channelData = buffer.getChannelData(channel);
        for (let i = 0; i < frameCount; i++) {
            channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
        }
    }
    return buffer;
}

function createBlob(data: Float32Array): Blob {
    const l = data.length;
    const int16 = new Int16Array(l);
    for (let i = 0; i < l; i++) {
        int16[i] = data[i] * 32768;
    }
    return {
        data: encode(new Uint8Array(int16.buffer)),
        mimeType: 'audio/pcm;rate=16000',
    };
}

// --- UI Icons ---

const GavelIcon: React.FC = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m14 13-7.5 7.5" /><path d="m16 11 6 6" /><path d="m8 8 2-2" /><path d="m12 12 6-6" /><path d="m6 6 2-2" /><path d="m10 10 6-6" /><path d="m12 2 4.5 4.5" /><path d="m7 7 4.5 4.5" /></svg>;

const LawyerIcon: React.FC = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 16.5c-2.28 0-4.12.78-4.12 1.75a1.75 1.75 0 0 0 3.5 0c0-.22-.03-.43-.08-.63" /><path d="M12 13.5c-2.28 0-4.12.78-4.12 1.75a1.75 1.75 0 0 0 3.5 0c0-.22-.03-.43-.08-.63" /><path d="M6 16.5c-2.28 0-4.12.78-4.12 1.75a1.75 1.75 0 0 0 3.5 0c0-.22-.03-.43-.08-.63" /><path d="M12 22v-6" /><path d="M6 22v-4.5" /><path d="M18 22v-4.5" /><path d="M17.5 7.5c.34-.5.66-1 .92-1.5C19.23 4.5 20.5 2 19.5 2c-.22 0-.44.03-.66.08L12 3.5 5.16 2.08a2.1 2.1 0 0 0-1.66.52c-.7.7-.38 2.37.52 3.9.27.46.59.92.92 1.5Z" /></svg>;

const DocumentIcon: React.FC = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></svg>;

const ScaleIcon: React.FC = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H7"></path></svg>;

const PlayIcon: React.FC<{ className?: string }> = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polygon points="5 3 19 12 5 21 5 3" /></svg>;

const StopIcon: React.FC<{ className?: string }> = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="18" height="18" x="3" y="3" rx="2" /><line x1="12" y1="3" x2="12" y2="21" /></svg>;

// --- Markdown Parser ---

function parseMarkdown(text: string): string {
    // Helper to apply inline styles
    const applyInlineStyles = (line: string) => {
        return line
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/__(.*?)__/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/_(.*?)_/g, '<em>$1</em>');
    };

    const lines = text.split('\n');
    let html = '';
    let inParagraph = false;

    const closeParagraph = () => {
        if (inParagraph) {
            html += '</p>';
            inParagraph = false;
        }
    };

    for (const line of lines) {
        // Block elements will close the current paragraph

        // Headings
        if (line.startsWith('#### ')) {
            closeParagraph();
            html += `<h4>${applyInlineStyles(line.substring(5))}</h4>`;
            continue;
        }
        if (line.startsWith('### ')) {
            closeParagraph();
            html += `<h3>${applyInlineStyles(line.substring(4))}</h3>`;
            continue;
        }
        if (line.startsWith('## ')) {
            closeParagraph();
            html += `<h2>${applyInlineStyles(line.substring(3))}</h2>`;
            continue;
        }
        if (line.startsWith('# ')) {
            closeParagraph();
            html += `<h1>${applyInlineStyles(line.substring(2))}</h1>`;
            continue;
        }

        // Horizontal Rule
        if (line.match(/^(---|___|\*\*\*)\s*$/)) {
            closeParagraph();
            html += '<hr />';
            continue;
        }

        // Blank line: ends current paragraph
        if (line.trim() === '') {
            closeParagraph();
            continue;
        }

        // If it's not a block element, it's part of a paragraph.
        if (!inParagraph) {
            html += '<p>';
            inParagraph = true;
        } else {
            // Add a line break for new lines within a paragraph block
            html += '<br />';
        }
        html += applyInlineStyles(line);
    }

    // Close any open paragraph at the end of the text
    closeParagraph();

    return html;
}

const LawyerPageVani: React.FC = () => {
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
    const [transcript, setTranscript] = useState<Message[]>([]);
    const [caseNotes, setCaseNotes] = useState<string>('');
    const [legalSummary, setLegalSummary] = useState<LegalSummary | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Fix: Replaced 'LiveSession' with 'any' because it is not an exported type from the library.
    const sessionPromiseRef = useRef<Promise<any> | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const inputAudioContextRef = useRef<AudioContext | null>(null);
    const outputAudioContextRef = useRef<AudioContext | null>(null);
    const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
    const mediaStreamSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const currentInputTranscriptionRef = useRef<string>('');
    const currentOutputTranscriptionRef = useRef<string>('');
    const outputAudioSources = useRef<Set<AudioBufferSourceNode>>(new Set());
    const nextStartTime = useRef(0);

    const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
    if (typeof API_KEY !== "string") {
        throw new Error("set REACT_APP_GEMINI_API_KEY in .env");
    }

    const cleanup = useCallback(() => {
        sessionPromiseRef.current?.then(session => session.close()).catch(() => { });
        sessionPromiseRef.current = null;
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        outputAudioSources.current.forEach(source => source.stop());
        outputAudioSources.current.clear();
        nextStartTime.current = 0;
        scriptProcessorRef.current?.disconnect();
        scriptProcessorRef.current = null;
        mediaStreamSourceRef.current?.disconnect();
        mediaStreamSourceRef.current = null;
        inputAudioContextRef.current?.close().catch(() => { });
        inputAudioContextRef.current = null;
        outputAudioContextRef.current?.close().catch(() => { });
        outputAudioContextRef.current = null;
        setConnectionStatus(ConnectionStatus.DISCONNECTED);
    }, []);

    const [isListenerMode, setIsListenerMode] = useState<boolean>(false);

    // Tool declaration for logging case notes
    const tools = [
        {
            functionDeclarations: [
                {
                    name: "log_case_note",
                    description: "Logs a key detail, fact, or observation about the case into the official records.",
                    parameters: {
                        type: Type.OBJECT,
                        properties: {
                            note: {
                                type: Type.STRING,
                                description: "The concise note to record (e.g., 'Client was married in 2015', 'Dispute is about 5 acres of land')."
                            }
                        },
                        required: ["note"]
                    }
                }
            ]
        }
    ];

    const generateLegalSummary = useCallback(async (notes: string) => {
        if (!notes) return;
        // Don't clear previous summary immediately to avoid flickering during updates
        try {
            const ai = new GoogleGenAI({ apiKey: API_KEY });
            const prompt = `Analyze the following legal case notes and provide a structured summary.

            Case Notes:
            ---
            ${notes}
            ---

            Provide your analysis in a JSON format with four keys:

            1.  "classification": A brief classification of the case (e.g., "Civil - Property Dispute", "Criminal - Theft").

            2.  "ipc": List potentially relevant sections of the Indian Penal Code (IPC). If none are clearly applicable, return exactly "Not Applicable". Do not explain why.

            3.  "bns": List potentially relevant sections of the Bharatiya Nyaya Sanhita (BNS). If none are clearly applicable, return exactly "Not Applicable". Do not explain why.

            4.  "analysis": A brief, professional legal observation or potential next step based on the facts (e.g., "The case hinges on the 2015 agreement...", "Immediate filing of FIR recommended...").
            `;

            const response = await ai.models.generateContent({
                model: 'gemini-2.5-flash', // Use Flash for speed
                contents: prompt,
                config: {
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: Type.OBJECT,
                        properties: {
                            classification: { type: Type.STRING },
                            ipc: { type: Type.STRING },
                            bns: { type: Type.STRING },
                            analysis: { type: Type.STRING },
                        },
                    },
                },
            });
            const summaryText = response.text?.trim();
            if (summaryText) {
                const summaryData = JSON.parse(summaryText) as LegalSummary;
                setLegalSummary(summaryData);
            }
        } catch (err) {
            console.error('Legal summary generation failed:', err);
            // Don't show error to user for background updates to avoid annoyance
        }
    }, [API_KEY]);

    // Debounce legal summary generation when notes change
    useEffect(() => {
        const timer = setTimeout(() => {
            if (caseNotes) {
                generateLegalSummary(caseNotes);
            }
        }, 3000); // Wait 3 seconds after last note update
        return () => clearTimeout(timer);
    }, [caseNotes, generateLegalSummary]);

    const handleDisconnect = useCallback(() => {
        cleanup();
    }, [cleanup]);

    const handleConnect = useCallback(async () => {
        setConnectionStatus(ConnectionStatus.CONNECTING);
        setError(null);
        setTranscript([]);
        setCaseNotes('');
        setLegalSummary(null);

        try {
            const ai = new GoogleGenAI({ apiKey: API_KEY });
            streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
            inputAudioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
            outputAudioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });

            const systemInstruction = isListenerMode
                ? 'You are a silent legal assistant. Your ONLY task is to listen to the conversation between a human lawyer and a client, and use the `log_case_note` tool to record important details. DO NOT speak, interrupt, or offer advice. Just listen and log notes.'
                : 'You are Vani, a professional and empathetic AI legal assistant from ALMT Legal. Your role is to listen carefully to the client\'s case details, ask clarifying questions, and take detailed notes. Whenever the client mentions a significant detail (timeline, person, fact, location, etc.), use the `log_case_note` tool to record it immediately. Maintain a professional and reassuring tone.';

            sessionPromiseRef.current = ai.live.connect({
                model: 'gemini-2.5-flash-native-audio-preview-09-2025',
                config: {
                    tools: tools,
                    responseModalities: [Modality.AUDIO],
                    speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Zephyr' } } },
                    systemInstruction: systemInstruction,
                    inputAudioTranscription: {},
                    outputAudioTranscription: {},
                },
                callbacks: {
                    onopen: () => {
                        setConnectionStatus(ConnectionStatus.CONNECTED);
                        mediaStreamSourceRef.current = inputAudioContextRef.current!.createMediaStreamSource(streamRef.current!);
                        scriptProcessorRef.current = inputAudioContextRef.current!.createScriptProcessor(4096, 1, 1);
                        scriptProcessorRef.current.onaudioprocess = (audioProcessingEvent) => {
                            const inputData = audioProcessingEvent.inputBuffer.getChannelData(0);
                            sessionPromiseRef.current?.then((session) => session.sendRealtimeInput({ media: createBlob(inputData) }));
                        };
                        mediaStreamSourceRef.current.connect(scriptProcessorRef.current);
                        scriptProcessorRef.current.connect(inputAudioContextRef.current!.destination);
                    },
                    onmessage: async (message: LiveServerMessage) => {
                        // Handle Tool Calls
                        const toolCall = message.toolCall;
                        if (toolCall && toolCall.functionCalls) {
                            const functionCalls = toolCall.functionCalls;
                            const toolResponses: any[] = [];

                            for (const call of functionCalls) {
                                if (call.name === 'log_case_note') {
                                    const args = call.args as { note: string };
                                    const newNote = args.note;
                                    // Append note as a bullet point
                                    setCaseNotes(prev => prev + `\n- ${newNote}`);

                                    toolResponses.push({
                                        id: call.id,
                                        name: call.name,
                                        response: { result: 'Note logged successfully' }
                                    });
                                }
                            }

                            // Send tool response back to model
                            if (toolResponses.length > 0) {
                                sessionPromiseRef.current?.then(session => session.sendToolResponse({
                                    functionResponses: toolResponses
                                }));
                            }
                            return; // Skip audio processing for tool calls if desired, or let it fall through
                        }

                        if (message.serverContent?.inputTranscription) currentInputTranscriptionRef.current += message.serverContent.inputTranscription.text;
                        if (message.serverContent?.outputTranscription) currentOutputTranscriptionRef.current += message.serverContent.outputTranscription.text;
                        if (message.serverContent?.turnComplete) {
                            const fullInput = currentInputTranscriptionRef.current.trim();
                            const fullOutput = currentOutputTranscriptionRef.current.trim();
                            // Fix: Explicitly create a typed array of messages to satisfy the Message interface,
                            // which requires the 'speaker' property to be a specific literal type ('Client' | 'AI Lawyer').
                            const newMessages: Message[] = [];
                            if (fullInput) {
                                newMessages.push({ speaker: 'Client', text: fullInput, timestamp: '' });
                            }
                            if (fullOutput) {
                                newMessages.push({ speaker: 'AI Lawyer', text: fullOutput, timestamp: '' });
                            }
                            if (newMessages.length > 0) {
                                setTranscript(prev => [...prev, ...newMessages]);
                            }
                            currentInputTranscriptionRef.current = '';
                            currentOutputTranscriptionRef.current = '';
                        }

                        const base64Audio = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
                        if (base64Audio) {
                            const audioContext = outputAudioContextRef.current!;
                            nextStartTime.current = Math.max(nextStartTime.current, audioContext.currentTime);
                            const audioBuffer = await decodeAudioData(decode(base64Audio), audioContext, 24000, 1);
                            const source = audioContext.createBufferSource();
                            source.buffer = audioBuffer;
                            source.connect(audioContext.destination);
                            source.addEventListener('ended', () => outputAudioSources.current.delete(source));
                            source.start(nextStartTime.current);
                            nextStartTime.current += audioBuffer.duration;
                            outputAudioSources.current.add(source);
                        }

                        if (message.serverContent?.interrupted) {
                            outputAudioSources.current.forEach(source => source.stop());
                            outputAudioSources.current.clear();
                            nextStartTime.current = 0;
                        }
                    },
                    onerror: (e: ErrorEvent) => {
                        console.error('Session error:', e);
                        setError('An error occurred with the connection.');
                        setConnectionStatus(ConnectionStatus.ERROR);
                        cleanup();
                    },
                    onclose: handleDisconnect,
                },
            });
        } catch (err) {
            console.error('Connection failed:', err);
            setError(err instanceof Error ? err.message : 'Failed to connect. Please check permissions and API key.');
            setConnectionStatus(ConnectionStatus.ERROR);
            cleanup();
        }
    }, [API_KEY, cleanup, handleDisconnect, generateLegalSummary, isListenerMode]);

    useEffect(() => () => cleanup(), [cleanup]);

    const renderNotes = () => {
        if (isLoading) return <div className="empty-state">Generating notes & analysis...</div>;
        if (caseNotes) return <div className="prose" dangerouslySetInnerHTML={{ __html: parseMarkdown(caseNotes) }} />;
        return <div className="empty-state">No case notes recorded yet. Begin your consultation to generate notes.</div>;
    };

    return (
        <div className="lawyer-vani-container">
            <header>
                <div className="header-content">
                    <div className="header-left">
                        <img src="/almt-legal.jpeg" alt="ALMT Legal" className="header-logo" />

                    </div>
                    <div className="header-divider"></div>
                    <div className="mode-controls-wrapper">
                        <div className="mode-segmented-control">
                            <button
                                className={`mode-option ${!isListenerMode ? 'active' : ''}`}
                                onClick={() => connectionStatus === ConnectionStatus.DISCONNECTED && setIsListenerMode(false)}
                                disabled={connectionStatus !== ConnectionStatus.DISCONNECTED}
                                title="Interactive mode: AI speaks and takes notes"
                            >
                                Lawyer Mode
                            </button>
                            <button
                                className={`mode-option ${isListenerMode ? 'active' : ''}`}
                                onClick={() => connectionStatus === ConnectionStatus.DISCONNECTED && setIsListenerMode(true)}
                                disabled={connectionStatus !== ConnectionStatus.DISCONNECTED}
                                title="Silent mode: AI only takes notes"
                            >
                                Listener Mode
                            </button>
                        </div>
                        <div className="mode-description">
                            {isListenerMode
                                ? "Vani silently records notes without interrupting the consultation."
                                : "Vani actively participates, asking questions and providing counsel."
                            }
                        </div>
                    </div>
                </div>
                <div className="header-bar"></div>
            </header>

            <main>
                {/* Left Column */}
                <div className="left-column">
                    <div className="column-header">
                        <DocumentIcon />
                        <div>
                            <h2>Case Notes & Proceedings</h2>
                            <div className="header-gradient"></div>
                        </div>
                    </div>
                    <div className="column-content">
                        {renderNotes()}
                    </div>
                </div>

                {/* Right Column */}
                <div className="right-column">
                    <div className="summary-card">
                        <div className="summary-header">
                            <ScaleIcon />
                            <div>
                                <h2>Legal Case Summary</h2>
                                <div className="summary-gradient"></div>
                            </div>
                        </div>

                        <div className="summary-items">
                            <div className="summary-item">
                                <h3>CASE CLASSIFICATION</h3>
                                <div className="item-divider"></div>
                                <p>{legalSummary?.classification || (isLoading ? 'Analyzing...' : 'Pending analysis...')}</p>
                            </div>
                            <div className="summary-item">
                                <h3>LEGAL ANALYSIS & OBSERVATIONS</h3>
                                <div className="item-divider"></div>
                                <p>{legalSummary?.analysis || (isLoading ? 'Analyzing...' : 'Pending analysis...')}</p>
                            </div>
                            <div className="summary-item">
                                <h3>INDIAN PENAL CODE (IPC) SECTIONS</h3>
                                <div className="item-divider"></div>
                                <p>{legalSummary?.ipc || (isLoading ? 'Analyzing...' : 'Awaiting legal review...')}</p>
                            </div>
                            <div className="summary-item">
                                <h3>BHARATIYA NYAYA SANHITA SECTIONS</h3>
                                <div className="item-divider"></div>
                                <p>{legalSummary?.bns || (isLoading ? 'Analyzing...' : 'Awaiting legal review...')}</p>
                            </div>
                        </div>
                    </div>
                    {error && <div className="error-message" role="alert"><strong>Error:</strong><span>{error}</span></div>}
                </div>
            </main>

            <footer>
                <div className="footer-controls">
                    {connectionStatus !== ConnectionStatus.CONNECTED ? (
                        <button onClick={handleConnect} disabled={isLoading || connectionStatus === ConnectionStatus.CONNECTING}>
                            <PlayIcon />
                        </button>
                    ) : (
                        <button onClick={handleDisconnect} className="connected">
                            <StopIcon />
                        </button>
                    )}
                </div>
                <p className="footer-status">
                    {isLoading ? 'Processing...' : (connectionStatus === ConnectionStatus.CONNECTED ? 'Consultation in Progress...' : 'Ready to Connect')}
                </p>
            </footer>
        </div>
    );
};

export default LawyerPageVani;
